import React, { useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { useAuth } from "hooks/useAuth";
import { ReactComponent as CrosspointLogo } from "assets/svg/crosspoint-logo.svg";
import { AuthenticateWithGoogleButton } from "components/AuthenticateWithGoogleButton";
import { useTranslation } from "react-i18next";
import { Button, Input } from "components/Shared";

import { toast } from "sonner";

import { Locations } from "interfaces/enums";
// import SignUpLangSelector from "components/SignUpLangSelector";
import EnvironmentSelector from "components/EnvironmentSelector";
import EnvironmentIndicator, { ShowIn } from "components/EnvironmentIndicator";
import { ISignInForm } from "interfaces";
import { useForm } from "react-hook-form";
import { Card, CardContent } from "components/ui/card";

const Login = () => {
  const { user, firebaseUser, newSignInGoogle, logout, newSignInPassword } =
    useAuth();
  const { search } = useLocation();

  // const [showPassword, setShowPassword] = useState(false)

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISignInForm>();

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const tokenExpired = urlParams.get("tokenExpired");
    if (tokenExpired) {
      toast.error(t("Token Expired. Please login again."));
    }
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSignIn = async (values: ISignInForm) => {
    try {
      await newSignInPassword(values.email, values.password);
    } catch (err) {
      console.error(err);
    }
  };

  if (firebaseUser && !firebaseUser.emailVerified) {
    return (
      <Navigate
        to={Locations["/verify-email"]}
        state={{ email: firebaseUser.email }}
      />
    );
  }

  return (
    <>
      {user ? (
        <Navigate to={Locations["/"]} replace={true} />
      ) : (
        <div className="bg-muted flex min-h-screen flex-col font-[google] items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <Card className="overflow-hidden p-0 border-none shadow-none sm:border sm:border-gray-details/60 sm:shadow-elevated rounded-2xl">
              <CardContent className="p-0 w-full">
                <div className="flex flex-col gap-8 px-8 py-10 sm:px-10 sm:py-12 w-full">
                  {/* <div className="justify-end absolute end-10 top-6">
                    <SignUpLangSelector theme="light" />
                  </div> */}

                  {/* Header */}
                  <div className="flex flex-col items-center gap-6">
                    <CrosspointLogo className="w-56" />
                    <div className="flex flex-col items-center gap-1.5">
                      <h1 className="text-xl font-semibold text-foreground font-[google]">
                        {t("Welcome back")}
                      </h1>
                      <p className="text-sm text-muted-foreground text-center font-[google]">
                        {t("Sign in to your account to continue")}
                      </p>
                    </div>
                  </div>

                  <EnvironmentIndicator hideIn={ShowIn.production}>
                    <EnvironmentSelector />
                  </EnvironmentIndicator>

                  {/* Sign in Form */}
                  <form
                    onSubmit={handleSubmit(submitSignIn)}
                    className="flex flex-col gap-5"
                  >
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="email"
                        className="text-sm  text-foreground"
                      >
                        {t("Email")}
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("name@company.com")}
                        {...register("email", { required: true })}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="password"
                          className="text-sm  text-foreground"
                        >
                          {t("Password")}
                        </label>
                        <Link
                          to={Locations["/reset-password"]}
                          className="text-sm  text-primary hover:underline"
                        >
                          {t("Forgot password?")}
                        </Link>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        placeholder={t("Enter your password")}
                        {...register("password", { required: true })}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full mt-1"
                      disabled={!isValid}
                    >
                      {t("Sign In")}
                    </Button>
                  </form>

                  {/* Divider */}
                  <div className="relative text-center text-sm text-muted-foreground after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center">
                    <span className="bg-card relative z-10 px-3">
                      {t("Or")}
                    </span>
                  </div>

                  <AuthenticateWithGoogleButton
                    buttonText={t("Sign in with Google")}
                    onClick={newSignInGoogle}
                  />
                </div>
              </CardContent>
            </Card>

            <p className="text-muted-foreground text-center text-xs text-balance pt-6 px-4 leading-relaxed">
              {t("By clicking continue, you agree to our")}{" "}
              <a
                href="https://www.crosspoint.global/terminos"
                className="text-primary  hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Terms and Conditions")}
              </a>{" "}
              {t("and")}{" "}
              <a
                href="https://www.crosspoint.global/privacy-policy"
                className="text-primary  hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Privacy Policy")}
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
